import { useMemo } from 'react';
import { useQuery } from 'react-query';

import { GetOneResult, IRequestPayload } from 'core/types';
import { corporateService } from 'corporate/services';
import { discountService } from 'corporate/services';
import { IDiscount, IVerticalMarket } from 'corporate/types';

export const useGetShippingMethods = (requestPayload?: IRequestPayload, enabled = true) => {
  const { data, isError, isLoading, isFetching } = useQuery(
    ['shippingMethods', requestPayload],
    ({ signal }) => corporateService.getShippingMethods(requestPayload, signal),
    {
      keepPreviousData: true,
      enabled,
    }
  );

  return {
    records: data?.records ?? [],
    count: data?.count ?? 0,
    isError,
    isLoading,
    isFetching,
  };
};

// Resolves a vertical market by its exact name (server search is fuzzy, so we
// exact-match client-side). No match -> null so callers can no-op gracefully.
export const useGetVerticalMarketByName = (
  name: string,
  enabled = true
): GetOneResult<IVerticalMarket | null> => {
  const { data, isError, isLoading } = useQuery(
    ['verticalMarketByName', name],
    ({ signal }) => corporateService.getVerticalMarketsAutocomplete({ q: name }, signal),
    { enabled: enabled && !!name }
  );

  const record = useMemo(
    () => (data?.records ?? []).find((market) => market.name === name) ?? null,
    [data, name]
  );

  return { record, isError, isLoading };
};

export const useGetDiscounts = (
  requestPayload?: IRequestPayload,
  enabled = true,
  onSuccess?: (data: { records: IDiscount[]; count: number }) => void
) => {
  const { data } = useQuery(
    ['discounts', requestPayload],
    ({ signal }) => discountService.getDiscounts<IDiscount>(requestPayload, signal),
    {
      enabled,
      onSuccess: (data) => {
        if (onSuccess) {
          onSuccess({ records: data?.records ?? [], count: data?.count ?? 0 });
        }
      },
    }
  );

  return {
    records: data?.records ?? [],
    count: data?.count ?? 0,
  };
};
